import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import * as styles from './tenure-insights.module.scss';
import { TA_DETAIL_PATH, TA_TABS, TA_TENURE_TABS, TA_YEARS_OPTIONS, YISG_AGING_REPORT_TYPE } from '../../talent-analytics.constant';
import BarChartCard from '../BarChartCard/BarChartCard';
import DropdownYears from '../DropdownYears/DropdownYears';
import { notification, Spin } from 'antd';
import { talentAnalyticApi } from '../../../../services/talentAnalyticApi';
import { cloneDeep, isEmpty } from 'lodash';
import YearInPositionTenureInsightsChart from '../YearInPositionTenureInsightsChart/YearInPositionTenureInsightsChart';
import { useSelector } from 'react-redux';
import CommonMasterFilters, { InitMasterFilter } from '../../../../components/CommonMasterFilters/CommonMasterFilters';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom';
import ViewReportTenureInsights from '../ViewReportTenureInsights/ViewReportTenureInsights';
import { USER_ACCESS_MESSAGE } from '../../../../utils/constants';
import TopTalentCategoryDynamics from '../TopTalentCategoryDynamics/TopTalentCategoryDynamics';
import { transformDataStackOffsetEqualSignChart } from '../../../../utils/helper';

const TenureInsights = ({ reportType }) => {
  const [currentTab, setCurrentTab] = useState(reportType || TA_TENURE_TABS[0]);
  const [loading, setLoading] = useState(false);
  const [tabFilterOptions, setTabFilterOptions] = useState({
    [TA_TENURE_TABS[0]]: TA_YEARS_OPTIONS,
    [TA_TENURE_TABS[1]]: TA_YEARS_OPTIONS,
    [TA_TENURE_TABS[2]]: [],
  });
  const [tabFilters, setTabFilters] = useState({
    [TA_TENURE_TABS[0]]: TA_YEARS_OPTIONS[0].value,
    [TA_TENURE_TABS[1]]: TA_YEARS_OPTIONS[0].value,
    [TA_TENURE_TABS[2]]: null,
  });
  const [data, setData] = useState({
    [TA_TENURE_TABS[0]]: {},
    [TA_TENURE_TABS[1]]: {},
    [TA_TENURE_TABS[2]]: {},
    [TA_TENURE_TABS[4]]: {},
  });
  const [updatedFilters, setUpdatedFilters] = useState([true, true, true, true, true]);
  const roleActive = useSelector((state) => state.user.roleActive);
  // const [businessUnitList, setBusinessUnitList] = useState([]);
  const [filterMasterData, setFilterMasterData] = useState(InitMasterFilter);
  const [currentChartData, setCurrentChartData] = useState(null);
  const location = useLocation();
  const hoveredValueRef = useRef(null);
  const resetHoveredValueRef = () => {
    hoveredValueRef.current = null;
  };

  const searchParams = new URLSearchParams(location.search);
  const tabIndex = searchParams.get('tab');
  useEffect(() => {
    if (tabIndex && !isNaN(tabIndex)) {
      setCurrentTab(TA_TENURE_TABS[tabIndex]);
    }
  }, [tabIndex]);
  const history = useHistory();

  const displayData = useMemo(() => {
    return data[currentTab];
  }, [currentTab, data]);

  const handleChangeTabFilter = (value) => {
    if (value === tabFilters[currentTab]) return;
    setTabFilters((prev) => {
      const cloneState = cloneDeep(prev);
      cloneState[currentTab] = value;
      return cloneState;
    });
    let index = 0;
    switch (currentTab) {
      case TA_TENURE_TABS[0]:
        index = 0;
        break;
      case TA_TENURE_TABS[1]:
        index = 1;
        break;
      case TA_TENURE_TABS[2]:
        index = 2;
        break;
      default:
        break;
    }
    setUpdatedFilters((prev) => {
      const cloneState = cloneDeep(prev);
      cloneState[index] = true;
      return cloneState;
    });
  };

  // update state for only call API when change common filters
  useEffect(() => {
    setUpdatedFilters([true, true, true, true, true]);
  }, [filterMasterData, tabIndex]);

  const handleViewReport = (data) => {
    setCurrentChartData(data);
    history.push(`${window.location.pathname}?report=${currentTab}`);
  };

  // Feature for get filters if need. Currently doesn't matter
  useEffect(() => {
    const getTabFilterOptions = async () => {
      try {
        const res = await talentAnalyticApi.getTenureFiltersOptions();
        setTabFilterOptions(
          res?.data?.result || { [TA_TENURE_TABS[0]]: TA_YEARS_OPTIONS, [TA_TENURE_TABS[1]]: TA_YEARS_OPTIONS, [TA_TENURE_TABS[2]]: {} }
        );
      } catch (error) {
        console.error(error);
      }
    };
    getTabFilterOptions();
  }, []);

  //  Year in Salary Grade Tab
  const getYearInSalaryGrade = useCallback(async () => {
    try {
      const rawYear = Number(tabFilters[TA_TENURE_TABS[0]]);
      const start_year = rawYear === 0 ? 0 : rawYear - 1;
      const end_year = rawYear === 0 || start_year > 4 ? null : start_year + 1;
      const body = {
        filter: { start_year, end_year },
        masterFilter: filterMasterData,
      };
      setLoading(true);
      const res = await talentAnalyticApi.getYearInSalaryGrade(body);
      if (res.status === 200) {
        setData((prev) => ({ ...prev, [TA_TENURE_TABS[0]]: res.data.result }));
      }
    } catch (error) {
      console.error(error);
      setData((prev) => ({
        ...prev,
        [TA_TENURE_TABS[0]]: {},
      }));
    } finally {
      setLoading(false);
      setUpdatedFilters((prev) => {
        const cloneState = cloneDeep(prev);
        cloneState[0] = false;
        return cloneState;
      });
    }
  }, [tabFilters[TA_TENURE_TABS[0]], filterMasterData]);

  useEffect(() => {
    if (currentTab !== TA_TENURE_TABS[0] || !updatedFilters[0]) return;
    getYearInSalaryGrade();
  }, [currentTab, updatedFilters[0]]);

  //  Year in Position Tab
  const getYearInPositionTenureInsights = useCallback(async () => {
    const rawYear = Number(tabFilters[TA_TENURE_TABS[1]]);
    const start_year = rawYear === 0 ? 0 : rawYear - 1;
    const end_year = rawYear === 0 || start_year > 4 ? null : start_year + 1;
    const payload = {
      filter: { start_year, end_year },
      masterFilter: filterMasterData,
    };

    try {
      setLoading(true);
      const res = await talentAnalyticApi.getYearInPositionTenureInsights(payload, roleActive.roleId);
      if (res.status === 200) {
        setData((prev) => ({ ...prev, [TA_TENURE_TABS[1]]: res.data.result }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setUpdatedFilters((prev) => {
        const cloneState = cloneDeep(prev);
        cloneState[1] = false;
        return cloneState;
      });
    }
  }, [tabFilters[TA_TENURE_TABS[1]], roleActive.roleId, filterMasterData]);

  useEffect(() => {
    if (currentTab !== TA_TENURE_TABS[1] || !updatedFilters[1]) return;
    getYearInPositionTenureInsights();
  }, [currentTab, updatedFilters[1], roleActive.roleId]);

  //  Get Business unit
  // useEffect(() => {
  //   const fetchBusinessUnit = async () => {
  //     try {
  //       const res = await talentAnalyticApi.fetchBusinessUnitList(roleActive.roleId);
  //       if (res.status === 200 && res?.data?.result) {
  //         setBusinessUnitList(!isEmpty(res?.data?.result) ? res?.data?.result : []);
  //         setTabFilters((prev) => {
  //           return {
  //             ...prev,
  //             [TA_TENURE_TABS[2]]: !isEmpty(res?.data?.result) ? res?.data?.result[0].value : null,
  //           };
  //         });
  //         setUpdatedFilters((prev) => {
  //           const cloneState = cloneDeep(prev);
  //           cloneState[2] = true;
  //           return cloneState;
  //         });
  //       }
  //     } catch (error) {
  //       console.error(error);
  //     }
  //   };
  //   fetchBusinessUnit();
  // }, [roleActive?.roleId]);

  const getYearInBusinessUnitOfTenureInsights = useCallback(async () => {
    const payload = {
      masterFilter: filterMasterData,
      // filter: {
      //   business_unit: !isEmpty(tabFilters[TA_TENURE_TABS[2]])
      //     ? tabFilters[TA_TENURE_TABS[2]]
      //     : !isEmpty(businessUnitList)
      //     ? businessUnitList[0].value
      //     : '',
      // },
      type: YISG_AGING_REPORT_TYPE.BY_BUSINESS_UNIT,
    };

    try {
      setLoading(true);
      const res = await talentAnalyticApi.fetchReportByYearInBusinessUnit(payload, roleActive.roleId);
      if (res.status === 200) {
        setData((prev) => ({ ...prev, [TA_TENURE_TABS[2]]: res.data.result }));
      }
    } catch (error) {
      console.error(error);
      setData((prev) => ({ ...prev, [TA_TENURE_TABS[2]]: {} }));
      notification.error({
        message: USER_ACCESS_MESSAGE.AN_UNEXPECTED_ERROR,
        duration: 5,
      });
    } finally {
      setLoading(false);
      setUpdatedFilters((prev) => {
        const cloneState = cloneDeep(prev);
        cloneState[2] = false;
        return cloneState;
      });
    }
  }, [tabFilters[TA_TENURE_TABS[2]], roleActive.roleId, filterMasterData]);

  useEffect(() => {
    if (currentTab !== TA_TENURE_TABS[2] || !updatedFilters[2]) return;
    getYearInBusinessUnitOfTenureInsights();
  }, [currentTab, updatedFilters[2], roleActive.roleId]);

  const getMaxEntry = (data) => {
    return data.flatMap((group) => group.values).reduce((max, current) => (current.value > max.value ? current : max));
  };

  //SUCCESSOR APPROACHING RETIREMENT TAB
  const getSuccessorApproachingRetirement = useCallback(async () => {
    const payload = {
      masterFilter: filterMasterData,
      type: YISG_AGING_REPORT_TYPE.SUCCESSORS_APPROACHING_RETIREMENT_DATE,
    };

    try {
      setLoading(true);
      const res = await talentAnalyticApi.fetchSuccessorApproachingRetirement(payload, roleActive.roleId);
      if (res.status === 200) {
        setData((prev) => ({ ...prev, [TA_TENURE_TABS[4]]: res.data.result }));
      }
    } catch (error) {
      console.error(error);
      setData((prev) => ({ ...prev, [TA_TENURE_TABS[4]]: {} }));
      notification.error({
        message: USER_ACCESS_MESSAGE.AN_UNEXPECTED_ERROR,
        duration: 5,
      });
    } finally {
      setLoading(false);
      setUpdatedFilters((prev) => {
        const cloneState = cloneDeep(prev);
        cloneState[4] = false;
        return cloneState;
      });
    }
  }, [roleActive.roleId, filterMasterData]);

  useEffect(() => {
    if (currentTab !== TA_TENURE_TABS[4] || !updatedFilters[4]) return;
    getSuccessorApproachingRetirement();
  }, [currentTab, updatedFilters[4], roleActive.roleId]);

  const onChangeTab = (tab, tabNumber) => {
    setCurrentTab(tab);
    history.push(`${TA_DETAIL_PATH}/${TA_TABS[2].path}?tab=${tabNumber}`);
  };

  const getRangeReport = (name) => {
    let tempVal = undefined;
    switch (name) {
      case 'Below 1 year':
        tempVal = 1;
        break;
      case '1 - 2 years':
        tempVal = 2;
        break;
      case '2 - 3 years':
        tempVal = 3;
        break;
      case '3 - 4 years':
        tempVal = 4;
        break;
      case '4 - 5 years':
        tempVal = 5;
        break;
      case '5 years & Above':
        tempVal = 6;
        break;
      default:
        break;
    }
    return tempVal;
  };

  return (
    <>
      <div className={styles.wrapper} style={reportType ? { display: 'none' } : {}}>
        <Spin spinning={loading}>
          <CommonMasterFilters setFilterFinderData={setFilterMasterData} filterFinderData={filterMasterData} />
          <div className={styles.contents}>
            <div className={styles.tabs}>
              {TA_TENURE_TABS.map((tab, tabIndex) => (
                <div key={tab} className={`${styles.tab} ${currentTab === tab ? styles.active : ''}`} onClick={() => onChangeTab(tab, tabIndex)}>
                  {tab}
                </div>
              ))}
            </div>

            {[TA_TENURE_TABS[0], TA_TENURE_TABS[1], TA_TENURE_TABS[2]].includes(currentTab) && (
              <>
                <div className={styles.years}>
                  <div>
                    Mean <span>{displayData?.means || 0} Years</span>
                  </div>
                  <div>
                    Median <span>{displayData?.median || 0} Years</span>
                  </div>
                </div>

                <div className={styles.yisg}>
                  <div className={styles.filters}>
                    {currentTab === TA_TENURE_TABS[0] && <div>Years In Salary Grade</div>}
                    {currentTab === TA_TENURE_TABS[1] && <div>Years In Position</div>}
                    {/* {currentTab === TA_TENURE_TABS[2] && <div>Business Unit:</div>} */}
                    {![TA_TENURE_TABS[2]].includes(currentTab) && (
                      <div className="ml-2">
                        <DropdownYears
                          placeholder="Select"
                          options={tabFilterOptions[currentTab]}
                          onChange={handleChangeTabFilter}
                          value={tabFilters[currentTab]}
                        />
                      </div>
                    )}
                  </div>
                  <div className={styles.totalTalents}>
                    <div>Total Talents</div>
                    <span>{displayData?.total_talents || 0}</span>
                  </div>
                </div>

                <div className={styles.charts}>
                  {currentTab === TA_TENURE_TABS[0] && (
                    <BarChartCard
                      data={displayData?.chart_data}
                      onViewReport={() => handleViewReport(displayData?.chart_data)}
                      chartType={TA_TENURE_TABS[0]}
                    />
                  )}
                  {currentTab === TA_TENURE_TABS[1] && (
                    <YearInPositionTenureInsightsChart
                      data={displayData?.chart_data}
                      onViewReport={handleViewReport}
                      hoveredValueRef={hoveredValueRef}
                    />
                  )}
                  {currentTab === TA_TENURE_TABS[2] && (
                    <div className={styles.chartWrapper}>
                      {!isEmpty(displayData?.chart_data) &&
                        displayData?.chart_data.map((data, index) => {
                          return (
                            <div key={index} className={styles.chartItem}>
                              <BarChartCard
                                data={{ ...data, no: index + 1 }}
                                onViewReport={() =>
                                  handleViewReport({
                                    ...data,
                                    min: data?.min + 1,
                                    business_unit: tabFilters[TA_TENURE_TABS[2]] || '',
                                    report_type: YISG_AGING_REPORT_TYPE.BY_BUSINESS_UNIT,
                                  })
                                }
                                chartType={TA_TENURE_TABS[2]}
                                maxEntryItem={getMaxEntry(displayData?.chart_data)}
                              />
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </>
            )}

            {[TA_TENURE_TABS[3]].includes(currentTab) && (
              <TopTalentCategoryDynamics
                tabFilters={tabFilters}
                currentTab={currentTab}
                setUpdatedFilters={setUpdatedFilters}
                updatedFilters={updatedFilters}
                masterFilter={filterMasterData}
                roleId={roleActive.roleId}
              />
            )}

            {TA_TENURE_TABS[4] === currentTab && (
              <>
                <div className={styles.totalTalentsSuccessors}>
                  <div>Total Talents</div>
                  <span>{displayData?.total_talents || 0}</span>
                </div>
                <div className={styles.chartWrapper}>
                  {!isEmpty(displayData?.chart_data) &&
                    transformDataStackOffsetEqualSignChart(displayData?.chart_data, 'readiness', 'count').map((data, index) => {
                      return (
                        <div key={index} className={styles.chartItem}>
                          <BarChartCard
                            data={{ ...data, no: index + 1 }}
                            onViewReport={() =>
                              handleViewReport({
                                ...data,
                                min: getRangeReport(data?.name),
                                report_type: YISG_AGING_REPORT_TYPE.SUCCESSORS_APPROACHING_RETIREMENT_DATE,
                              })
                            }
                            chartType={TA_TENURE_TABS[4]}
                            maxEntryItem={getMaxEntry(transformDataStackOffsetEqualSignChart(displayData?.chart_data, 'readiness', 'count'))}
                          />
                        </div>
                      );
                    })}
                </div>
              </>
            )}
          </div>
        </Spin>
      </div>
      {reportType && (
        <ViewReportTenureInsights
          reportType={currentTab}
          filterMasterData={filterMasterData}
          tabFilters={tabFilters}
          totalTalent={displayData?.total_talents}
          currentChartData={currentChartData}
          resetHoveredValueRef={resetHoveredValueRef}
        />
      )}
    </>
  );
};

export default TenureInsights;

TenureInsights.propTypes = {};
